import type { ConflictApiError, CurationTool, CurationToolCrudService  } from '@empathyco/playboard-core-domain'
import type { ComputedRef, Ref } from 'vue'
import type { RegularFilter, RegularFilterSelectedValues } from '../models'
import type { CrudServiceProvider, DomainServiceProvider } from '../providers/interfaces'
import type { CurationToolDomainService } from '../services/interfaces'
import type { NormalizeQueryComposable } from './use-normalize-query'
import type { IToolingFormComposable } from './use-tooling-form'
import { deepMerge } from '@empathyco/x-deep-merge'
import { computed, ref } from 'vue'
import { CONTAINER } from '../config/inversify.config'
import { TYPES } from '../config/inversify.types'
import { FilterIdEnum } from '../enums'
import { FiltersUtils } from '../utils/filters.utils'
import { useNormalizeQuery } from './use-normalize-query'

export interface CurationToolCriteriaComposable<ItemType extends CurationTool> {
  userQuery: NormalizeQueryComposable['userQuery']
  normalizedQuery: NormalizeQueryComposable['normalizedQuery']
  itemQuery: ComputedRef<string | undefined>
  criteriaIsValid: ComputedRef<boolean>
  conflictItem: ComputedRef<ItemType | null>
  conflictApiError: Ref<ConflictApiError | undefined>
  updatePivots: (
    pivots: ItemType['criteria']['pivots'],
    filter: RegularFilter,
    selectedValues: RegularFilterSelectedValues,
  ) => ItemType['criteria']['pivots']
  setCriteria: (value: ItemType['criteria'], preserveRules?: boolean) => Promise<void>
}

export function useCurationToolCriteria<ItemType extends CurationTool>({
  featureId,
  item,
  pivotsConfig,
}: {
  featureId: IToolingFormComposable<ItemType>['featureId']
  item: IToolingFormComposable<ItemType>['item']
  pivotsConfig: IToolingFormComposable<ItemType>['pivotsConfig']
}): CurationToolCriteriaComposable<ItemType> {
  const domainService = CONTAINER.get<DomainServiceProvider>(TYPES.DomainServiceProvider)(
    featureId.value,
  ) as CurationToolDomainService
  const crudService = CONTAINER.get<CrudServiceProvider>(TYPES.CrudServiceProvider)(
    featureId.value,
  ) as unknown as CurationToolCrudService<CurationTool>
  const criteriaItems: { [key: string]: ItemType | null } = {}

  const { userQuery, normalizedQuery, setNormalizedQuery } = useNormalizeQuery()

  const itemQuery = computed<string | undefined>(() => item.value.criteria.queries[0])

  function isCriteriaValid(criteria: ItemType['criteria']): boolean {
    return domainService.isCriteriaValid(criteria, pivotsConfig.value)
  }

  const criteriaIsValid = computed<boolean>(() => isCriteriaValid(item.value.criteria))

  /**
   * Function to generate a unique criteria ID based on queries and pivots
   */
  function generateCriteriaId(criteria: ItemType['criteria']): string {
    const query = criteria.queries[0]
    const sortedPivotValues = criteria.pivots
      .slice()
      .sort((pivot1, pivot2) => pivot1.name.localeCompare(pivot2.name))
      .reduce<Array<string>>((result, pivot) => {
        if (pivot.values.length) {
          result.push(pivot.values[0])
        }
        return result
      }, [])
    return [query, ...sortedPivotValues].join('-')
  }

  const conflictItem = computed<ItemType | null>(() => null)

  const conflictApiError = ref<ConflictApiError>()

  function updatePivots(
    pivots: ItemType['criteria']['pivots'],
    filter: RegularFilter,
    selectedValues: RegularFilterSelectedValues = [],
  ): ItemType['criteria']['pivots'] {
    const newPivots = structuredClone(pivots)
    const name = FiltersUtils.getEncodedId(filter.config)
    const values = selectedValues.map(selectedValue =>
      FiltersUtils.encodeValue(filter.config, selectedValue),
    )
    const pivot = { name, values }
    const pivotIndex = newPivots.findIndex(currentPivot => currentPivot.name === pivot.name)
    if (pivotIndex < 0) {
      newPivots.push(pivot)
    } else {
      newPivots.splice(pivotIndex, 1, pivot)
    }
    return newPivots
  }

  async function loadItemMatchingCriteria(
    criteria: ItemType['criteria'],
  ): Promise<ItemType | null> {
    const criteriaId = generateCriteriaId(criteria)
    if (isCriteriaValid(criteria)) {
      const {
        queries: [query],
        pivots,
      } = criteria
      const response = await crudService.findItemsMatchingCriteria({
        enabled: true,
        query,
        ...pivots.reduce(
          (result, pivot) => ({
            ...result,
            [pivot.name]: pivot.values,
          }),
          {},
        ),
      })
      criteriaItems[criteriaId] = response.results[0] as ItemType
    }
    return criteriaItems[criteriaId]
  }

  async function setCriteria(value: ItemType['criteria'], preserveRules = false): Promise<void> {
    item.value.criteria.queries = value.queries

    const {
      queries: [query],
      pivots,
    } = value
    await setNormalizedQuery({
      query,
      lang: pivots.find(pivot => pivot.name === FilterIdEnum.LANG)?.values[0],
    })
    const normalizedCriteria = {
      ...value,
      queries: query ? [normalizedQuery.value ?? query] : [],
    }
    const itemMatchingCriteria = await loadItemMatchingCriteria(normalizedCriteria)
    item.value = deepMerge({}, item.value, {
      criteria: normalizedCriteria,
      data: {
        rules: preserveRules ? item.value.data.rules : [],
        indexed: itemMatchingCriteria?.data.indexed ?? [],
      },
    })
  }

  return {
    conflictApiError,
    conflictItem,
    criteriaIsValid,
    itemQuery,
    normalizedQuery,
    setCriteria,
    updatePivots,
    userQuery,
  }
}
